<template>
  <base-drawer
    class="bss-drawer"
    :show="show"
    title="Booking slot settings"
    width="520px"
    @close="onCloseRequest"
  >
    <!-- Loading skeleton -->
    <template v-if="loading">
          <div class="skeleton-section mb-3">
            <div class="skel" style="width: 45%; height: 14px; margin-bottom: 16px;"></div>
            <div v-for="n in 4" :key="'skel-sys-' + n" class="d-flex align-items-center mb-2">
              <span class="skel skel--dot mr-2"></span>
              <span class="skel skel--text flex-grow-1"></span>
              <span class="skel skel--btn mr-1"></span>
              <span class="skel skel--btn"></span>
            </div>
          </div>
          <div class="skeleton-section mb-3">
            <div class="skel" style="width: 40%; height: 14px; margin-bottom: 16px;"></div>
            <div v-for="n in 2" :key="'skel-cus-' + n" class="d-flex align-items-center mb-2">
              <span class="skel skel--dot mr-2"></span>
              <span class="skel skel--text flex-grow-1"></span>
              <span class="skel skel--btn"></span>
            </div>
          </div>
          <div class="skeleton-section mb-3">
            <div class="skel" style="width: 35%; height: 14px; margin-bottom: 16px;"></div>
            <div class="d-flex align-items-center">
              <span class="skel skel--switch mr-2"></span>
              <span class="skel skel--text flex-grow-1"></span>
            </div>
          </div>
          <div class="skeleton-section">
            <div class="skel" style="width: 50%; height: 14px; margin-bottom: 16px;"></div>
            <div v-for="n in 2" :key="'skel-if-' + n" class="d-flex align-items-center mb-2">
              <span class="skel skel--dot mr-2"></span>
              <span class="skel skel--text flex-grow-1"></span>
              <span class="skel skel--switch mr-2"></span>
              <span class="skel skel--btn"></span>
            </div>
          </div>
    </template>

    <!-- Body -->
    <template v-else>

          <!-- Default statuses -->
          <drawer-section
            title="Default statuses"
            icon="icon-palette"
            collapsible
            :expanded="expandedSections.defaults"
            @update:expanded="expandedSections.defaults = $event"
          >
            <template #header-actions>
              <span v-if="modifiedSystemStatusCount > 0" class="section-badge section-badge--accent">
                {{ modifiedSystemStatusCount }} modified
              </span>
            </template>
            <info-hint>Customize colors for built-in booking statuses. These cannot be deleted or renamed.</info-hint>

            <div class="status-list">
              <div
                v-for="(status, index) in workingSystemStatuses"
                :key="'sys-' + status.status_key"
                class="status-item d-flex align-items-center"
              >
                <span class="status-color-dot mr-2" :style="{ backgroundColor: status.color }"></span>
                <span class="status-name flex-grow-1">{{ status.display_name }}</span>
                <el-color-picker v-model="status.color" size="small" class="mr-2" />
                <el-color-picker v-model="status.text_color" size="small" />
                <base-button
                  v-if="isSystemStatusModified(status)"
                  link
                  type="default"
                  size="sm"
                  class="ml-2 text-white"
                  title="Reset to default"
                  @click="resetSystemStatus(index)"
                >
                  <i class="tim-icons icon-refresh-01"></i>
                </base-button>
              </div>
            </div>
          </drawer-section>

          <!-- Custom statuses -->
          <drawer-section
            title="Custom statuses"
            icon="icon-tag"
            collapsible
            :expanded="expandedSections.custom"
            @update:expanded="expandedSections.custom = $event"
          >
            <template #header-actions>
              <span class="section-badge">{{ workingStatuses.length }}</span>
            </template>
            <info-hint>Create custom statuses to track booking progress (e.g., Checked in, No-show). Slots with a custom status are exempt from autoblocking rules. By default they stay hidden from the customer-facing booking site, but each status can be set to show online as bookable or sold out.</info-hint>

            <div v-if="workingStatuses.length > 0" class="status-list">
              <div
                v-for="(status, index) in workingStatuses"
                :key="status.id || ('new-' + index)"
                class="status-item d-flex align-items-center"
              >
                <span class="status-color-dot mr-2" :style="{ backgroundColor: status.color }"></span>
                <span class="status-name flex-grow-1">{{ status.display_name }}</span>
                <badge class="mr-2" :type="onlineDisplayBadge(status.online_display_mode).type">
                  {{ onlineDisplayBadge(status.online_display_mode).label }}
                </badge>
                <el-dropdown trigger="click">
                  <base-button link type="primary" size="sm">
                    <i style="font-size: large" class="tim-icons el-icon-more"></i>
                  </base-button>
                  <template #dropdown>
                    <el-dropdown-menu>
                      <a class="dropdown-item text-default mb-2" href="#" @click.prevent="editCustomStatus(index)">
                        <i class="tim-icons text-default icon-pencil mr-2"></i> Edit
                      </a>
                      <a class="dropdown-item text-default" href="#" @click.prevent="deleteCustomStatus(index)">
                        <i class="tim-icons text-default icon-trash-simple mr-2"></i> Delete
                      </a>
                    </el-dropdown-menu>
                  </template>
                </el-dropdown>
              </div>
            </div>
            <div v-else class="status-empty">
              No custom statuses created yet.
            </div>

            <!-- Inline add/edit form -->
            <div v-if="showAddStatusForm" class="custom-status-form mt-3">
              <base-input
                ref="statusNameInput"
                label="Status name"
                v-model="customStatusForm.display_name"
                placeholder="e.g., Checked in, No-show"
                @input="generateStatusKey"
              />
              <div class="row">
                <div class="col-6">
                  <label class="d-block mb-1">Background</label>
                  <el-color-picker v-model="customStatusForm.color" />
                </div>
                <div class="col-6">
                  <label class="d-block mb-1">Text</label>
                  <el-color-picker v-model="customStatusForm.text_color" />
                </div>
              </div>

              <!-- Booking site display -->
              <div class="toggle-row mb-3">
                <el-switch active-color="#e14eca" v-model="showOnline" />
                <label class="mb-0 ml-2" style="cursor: pointer" @click="showOnline = !showOnline">
                  Show on booking site
                </label>
              </div>
              <template v-if="showOnline">
                <segmented-toggle
                  size="sm"
                  v-model="customStatusForm.online_display_mode"
                  :options="onlineDisplayModeOptions"
                />
                <info-hint class="mt-2">
                  {{ customStatusForm.online_display_mode === 'bookable'
                    ? 'Customers can book slots with this status, like a normal open slot.'
                    : 'Slots show but can\'t be booked. Customers see your booking site\'s sold out text and colors.' }}
                </info-hint>
              </template>

              <div class="mt-3 d-flex" style="gap: 8px;">
                <base-button size="sm" type="secondary" @click="cancelStatusForm">Cancel</base-button>
                <base-button
                  size="sm"
                  type="primary"
                  @click="saveCustomStatus"
                  :disabled="!customStatusForm.display_name"
                >
                  {{ editingStatusIndex !== null ? 'Update' : 'Create' }}
                </base-button>
              </div>
            </div>

            <base-button
              v-if="!showAddStatusForm"
              size="sm"
              type="primary"
              class="mt-2"
              @click="openAddStatusForm"
            >
              <i class="tim-icons icon-simple-add mr-1"></i> Add status
            </base-button>
          </drawer-section>

          <!-- Customer fields -->
          <drawer-section
            title="Customer fields"
            icon="icon-single-02"
            collapsible
            :expanded="expandedSections.customerFields"
            @update:expanded="expandedSections.customerFields = $event"
          >
            <template #header-actions>
              <span v-if="customerFieldRequiredCount > 0" class="section-badge section-badge--accent">
                {{ customerFieldRequiredCount }} required
              </span>
            </template>
            <info-hint>Built-in fields collected with every booking. Choose which ones must be filled in when creating bookings internally.</info-hint>

            <div class="toggle-row">
              <el-switch
                active-color="#e14eca"
                v-model="workingRequirePhoneInternally"
              />
              <label
                class="mb-0 ml-2"
                style="cursor: pointer"
                @click="workingRequirePhoneInternally = !workingRequirePhoneInternally"
              >
                Require phone number on staff-created bookings
              </label>
            </div>
          </drawer-section>

          <!-- Internal booking fields -->
          <drawer-section
            v-if="customFieldsAllowed"
            title="Internal booking fields"
            icon="icon-paper"
            collapsible
            :expanded="expandedSections.internalFields"
            @update:expanded="expandedSections.internalFields = $event"
          >
            <template #header-actions>
              <span class="section-badge">{{ workingInternalFields.length }}</span>
            </template>
            <internal-booking-fields
              v-model="workingInternalFields"
              :library-fields="customFieldLibrary"
              :loading="loading"
            />
          </drawer-section>
    </template>

    <!-- Footer -->
    <template #footer>
      <div class="footer-actions">
        <base-button type="secondary" size="sm" @click="onClose">Cancel</base-button>
        <base-button
          type="primary"
          size="sm"
          :loading="saving"
          :disabled="showAddStatusForm || loading"
          @click="save"
        >
          Save
        </base-button>
      </div>
    </template>
  </base-drawer>
</template>

<script>
import { API_LOCATION } from '@/api-config';
import axios from 'axios';
import { Auth } from 'aws-amplify';
import swal from 'sweetalert2';
import { ColorPicker, Dropdown, DropdownMenu, DropdownItem, Switch } from 'element-ui';
import InternalBookingFields from '@/components/CustomFields/InternalBookingFields.vue';
import Badge from '@/components/Badge';
import { getActiveGroupId, patchActiveGroup } from '@/util/activeGroup';

// Mirrors the booking_slot_status_types.online_display_mode ENUM. Controls how a
// custom-status slot renders on the customer booking site.
const ONLINE_DISPLAY = { HIDDEN: 'hidden', BOOKABLE: 'bookable', NON_BOOKABLE: 'non_bookable' };
// A slot stores its status key in booking_slots.status (varchar(45)); a longer
// key is cut there and never matches its status type again (OTC-3010).
const MAX_STATUS_KEY_LENGTH = 45;

export default {
  name: 'BookingSlotSettingsDrawer',
  components: {
    InternalBookingFields,
    Badge,
    [ColorPicker.name]: ColorPicker,
    [Dropdown.name]: Dropdown,
    [DropdownMenu.name]: DropdownMenu,
    [DropdownItem.name]: DropdownItem,
    [Switch.name]: Switch
  },
  props: {
    show: { type: Boolean, default: false }
  },
  data() {
    return {
      companyId: localStorage.getItem('companyId'),
      companyGroupId: JSON.parse(localStorage.getItem('group') || '{}').id,
      // 'cancelled' is excluded as it doesn't appear on the calendar.
      // Sorted alphabetically by display_name.
      DEFAULT_SYSTEM_STATUSES: [
        { status_key: 'available', display_name: 'Available', color: '#FFFFFF', text_color: '#1E1E2F' },
        { status_key: 'blocked', display_name: 'Blocked', color: '#D44872', text_color: '#FFFFFF' },
        { status_key: 'booked', display_name: 'Booked', color: '#1D8CF8', text_color: '#FFFFFF' },
        { status_key: 'call_to_book', display_name: 'Call to book', color: '#FB6340', text_color: '#FFFFFF' },
        { status_key: 'completed', display_name: 'Completed', color: '#2DCE89', text_color: '#FFFFFF' },
        { status_key: 'expired', display_name: 'Passed', color: '#525F7F', text_color: '#FFFFFF' },
        { status_key: 'requestable', display_name: 'Requestable', color: '#FFD600', text_color: '#1E1E2F' },
        { status_key: 'requested', display_name: 'Requested', color: '#00D6B4', text_color: '#1E1E2F' }
      ],
      loading: false,
      saving: false,
      workingSystemStatuses: [],
      customStatuses: [],
      workingStatuses: [],
      workingRequirePhoneInternally: false,
      workingInternalFields: [],
      customFieldLibrary: [],
      customFieldsAllowed: true,
      expandedSections: {
        defaults: true,
        custom: false,
        customerFields: false,
        internalFields: false
      },
      showAddStatusForm: false,
      editingStatusIndex: null,
      onlineDisplayModeOptions: [
        { value: ONLINE_DISPLAY.BOOKABLE, label: 'Bookable' },
        { value: ONLINE_DISPLAY.NON_BOOKABLE, label: 'Sold out' }
      ],
      customStatusForm: {
        status_key: '',
        display_name: '',
        color: '#6B7280',
        text_color: '#FFFFFF',
        is_blocked: true,
        online_display_mode: ONLINE_DISPLAY.HIDDEN
      }
    };
  },
  computed: {
    modifiedSystemStatusCount() {
      return this.workingSystemStatuses.filter(s => this.isSystemStatusModified(s)).length;
    },
    customerFieldRequiredCount() {
      return this.workingRequirePhoneInternally ? 1 : 0;
    },
    // "Show on booking site" switch: off = hidden; turning it on defaults to
    // non-bookable (the common "Sold out" FOMO case), then the segmented toggle
    // lets staff switch to bookable.
    showOnline: {
      get() {
        return this.customStatusForm.online_display_mode !== ONLINE_DISPLAY.HIDDEN;
      },
      set(val) {
        this.customStatusForm.online_display_mode = val
          ? ONLINE_DISPLAY.NON_BOOKABLE
          : ONLINE_DISPLAY.HIDDEN;
      }
    }
  },
  watch: {
    show(newVal) {
      if (newVal) {
        document.addEventListener('keydown', this._onEscKey);
        this.open();
      } else {
        document.removeEventListener('keydown', this._onEscKey);
      }
    }
  },
  created() {
    this._onEscKey = (e) => {
      if (e.key === 'Escape' && !this.showAddStatusForm) this.onClose();
    };
  },
  beforeDestroy() {
    document.removeEventListener('keydown', this._onEscKey);
  },
  methods: {
    async getAuthToken() {
      const user = await Auth.currentAuthenticatedUser();
      return user.signInUserSession.idToken.jwtToken;
    },

    async open() {
      this.showAddStatusForm = false;
      this.editingStatusIndex = null;
      this.customFieldsAllowed = true;
      this.customStatusForm = {
        status_key: '',
        display_name: '',
        color: '#6B7280',
        text_color: '#FFFFFF',
        is_blocked: true,
        online_display_mode: ONLINE_DISPLAY.HIDDEN
      };

      this.loading = true;
      try {
        const token = await this.getAuthToken();
        const config = { headers: { Authorization: token } };
        const settingsUrl = `${API_LOCATION}companies/${this.companyId}/groups/${this.companyGroupId}/booking-slot-settings`;
        const libraryUrl = `${API_LOCATION}companies/${this.companyId}/groups/${this.companyGroupId}/custom-fields?count=100&offset=0&archived=0`;

        // Library fetch is gated on `custom_fields_configure` permission. If the
        // user lacks it (e.g. custom role with booking_slot_update only), the call
        // 401s — handle that locally so the statuses fetch still populates the drawer.
        // The Internal booking fields section is hidden when this happens.
        const [settingsRes, libraryRes] = await Promise.all([
          axios.get(settingsUrl, config),
          axios.get(libraryUrl, config).catch(err => {
            this.customFieldsAllowed = false;
            return { data: { custom_fields: [] } };
          })
        ]);

        // Statuses
        const allCustomStatuses = settingsRes.data.custom_statuses || [];
        this.customStatuses = allCustomStatuses.filter(s => s.is_active !== 0);

        const systemOverrides = settingsRes.data.system_status_overrides || [];
        const overrideMap = new Map(systemOverrides.map(o => [o.status_key, o]));

        this.workingSystemStatuses = this.DEFAULT_SYSTEM_STATUSES.map(def => {
          const override = overrideMap.get(def.status_key);
          if (override) {
            return { ...def, color: override.color, text_color: override.text_color, id: override.id };
          }
          return { ...def };
        });

        this.workingStatuses = JSON.parse(JSON.stringify(this.customStatuses));

        // Customer fields
        this.workingRequirePhoneInternally = !!settingsRes.data.require_phone_internally;

        // Internal booking fields library + attachments
        this.customFieldLibrary = ((libraryRes.data || {}).custom_fields || []).map(f => ({
          id: f.id,
          label: f.label,
          type_id: f.type,
          add_alert: f.add_alert
        }));

        this.workingInternalFields = (settingsRes.data.internal_custom_fields || []).map(f => ({
          id: f.id,
          custom_field_id: f.custom_field_id,
          label: f.label,
          type_id: f.type_id != null ? f.type_id : f.type,
          required: f.required,
          add_alert: f.add_alert,
          position: f.position
        }));
      } catch (error) {
        console.log('Settings not available:', error.message);
        this.customStatuses = [];
        this.workingSystemStatuses = this.DEFAULT_SYSTEM_STATUSES.map(def => ({ ...def }));
        this.workingStatuses = [];
        this.workingRequirePhoneInternally = false;
        this.customFieldLibrary = [];
        this.workingInternalFields = [];
      } finally {
        this.loading = false;
      }
    },

    openAddStatusForm() {
      this.editingStatusIndex = null;
      this.customStatusForm = {
        status_key: '',
        display_name: '',
        color: '#6B7280',
        text_color: '#FFFFFF',
        is_blocked: true,
        online_display_mode: ONLINE_DISPLAY.HIDDEN
      };
      this.expandedSections.custom = true;
      this.showAddStatusForm = true;
      this.focusStatusNameInput();
    },

    cancelStatusForm() {
      this.showAddStatusForm = false;
      this.editingStatusIndex = null;
      this.customStatusForm = {
        status_key: '',
        display_name: '',
        color: '#6B7280',
        text_color: '#FFFFFF',
        is_blocked: true,
        online_display_mode: ONLINE_DISPLAY.HIDDEN
      };
    },

    editCustomStatus(index) {
      this.editingStatusIndex = index;
      const status = this.workingStatuses[index];
      this.customStatusForm = {
        status_key: status.status_key,
        display_name: status.display_name,
        color: status.color,
        text_color: status.text_color,
        is_blocked: !!status.is_blocked,
        online_display_mode: status.online_display_mode || ONLINE_DISPLAY.HIDDEN
      };
      this.expandedSections.custom = true;
      this.showAddStatusForm = true;
      this.focusStatusNameInput();
    },

    focusStatusNameInput() {
      this.$nextTick(() => {
        const ref = this.$refs.statusNameInput;
        const input = ref && ref.$el && ref.$el.querySelector('input');
        if (input) input.focus();
      });
    },

    generateStatusKey() {
      if (this.editingStatusIndex === null) {
        this.customStatusForm.status_key = this.customStatusForm.display_name
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, '_')
          .replace(/^_|_$/g, '')
          .slice(0, MAX_STATUS_KEY_LENGTH)
          .replace(/_$/, '');
      }
    },

    // Customer booking-site display config shown as a pill on each status row.
    onlineDisplayBadge(mode) {
      switch (mode) {
        case ONLINE_DISPLAY.BOOKABLE:
          return { label: 'Bookable', type: 'success' };
        case ONLINE_DISPLAY.NON_BOOKABLE:
          return { label: 'Sold out', type: 'warning' };
        default:
          return { label: 'Hidden', type: 'default' };
      }
    },

    saveCustomStatus() {
      const name = this.customStatusForm.display_name.trim();
      const key = this.customStatusForm.status_key;
      if (!name || !key) {
        // generateStatusKey strips non-alphanumerics; inputs like "!!!" produce an empty key.
        swal('Invalid name', 'Please enter a status name with at least one letter or number.', 'warning');
        return;
      }
      const reservedKeys = ['available', 'booked', 'blocked', 'completed', 'cancelled', 'expired', 'call_to_book', 'checked', 'requested', 'requestable'];
      if (reservedKeys.includes(key)) {
        swal('Reserved status', `"${name}" conflicts with a built-in status. Please choose a different name.`, 'warning');
        return;
      }
      const duplicateIndex = this.workingStatuses.findIndex(
        (s, i) => s.display_name.toLowerCase() === name.toLowerCase() && i !== this.editingStatusIndex
      );
      if (duplicateIndex !== -1) {
        swal('Duplicate status', `A status named "${name}" already exists.`, 'warning');
        return;
      }

      const entry = {
        status_key: this.customStatusForm.status_key,
        display_name: name,
        color: this.customStatusForm.color,
        text_color: this.customStatusForm.text_color,
        is_blocked: this.customStatusForm.is_blocked ? 1 : 0,
        online_display_mode: this.customStatusForm.online_display_mode || ONLINE_DISPLAY.HIDDEN
      };

      if (this.editingStatusIndex !== null) {
        entry.id = this.workingStatuses[this.editingStatusIndex].id;
        this.$set(this.workingStatuses, this.editingStatusIndex, entry);
      } else {
        this.workingStatuses.push(entry);
      }

      this.showAddStatusForm = false;
      this.editingStatusIndex = null;
    },

    deleteCustomStatus(index) {
      this.workingStatuses.splice(index, 1);
      if (this.editingStatusIndex !== null) {
        if (this.editingStatusIndex === index) {
          this.showAddStatusForm = false;
          this.editingStatusIndex = null;
        } else if (this.editingStatusIndex > index) {
          this.editingStatusIndex--;
        }
      }
    },

    isSystemStatusModified(status) {
      const defaultStatus = this.DEFAULT_SYSTEM_STATUSES.find(d => d.status_key === status.status_key);
      if (!defaultStatus) return false;
      return status.color !== defaultStatus.color || status.text_color !== defaultStatus.text_color;
    },

    resetSystemStatus(index) {
      const status = this.workingSystemStatuses[index];
      const defaultStatus = this.DEFAULT_SYSTEM_STATUSES.find(d => d.status_key === status.status_key);
      if (defaultStatus) {
        this.$set(this.workingSystemStatuses, index, {
          ...status,
          color: defaultStatus.color,
          text_color: defaultStatus.text_color
        });
      }
    },

    async save() {
      this.saving = true;
      try {
        const token = await this.getAuthToken();
        const config = { headers: { Authorization: token } };
        const url = `${API_LOCATION}companies/${this.companyId}/groups/${this.companyGroupId}/booking-slot-settings`;

        const modifiedSystemStatuses = this.workingSystemStatuses
          .filter(s => this.isSystemStatusModified(s) || s.id)
          .map(s => ({
            ...(s.id ? { id: s.id } : {}),
            status_key: s.status_key,
            display_name: s.display_name,
            color: s.color,
            text_color: s.text_color
          }));

        const payload = {
          system_status_overrides: modifiedSystemStatuses,
          custom_statuses: this.workingStatuses.map((s, i) => ({
            ...(s.id ? { id: s.id } : {}),
            status_key: s.status_key,
            display_name: s.display_name,
            color: s.color,
            text_color: s.text_color,
            is_blocked: s.is_blocked ? 1 : 0,
            online_display_mode: s.online_display_mode || ONLINE_DISPLAY.HIDDEN,
            position: i + 1
          })),
          require_phone_internally: this.workingRequirePhoneInternally ? 1 : 0
        };

        // Only include internal_custom_fields when the section is visible — users
        // without custom_fields_configure permission shouldn't touch this list.
        if (this.customFieldsAllowed) {
          payload.internal_custom_fields = this.workingInternalFields.map((f, i) => ({
            ...(f.id ? { id: f.id } : {}),
            custom_field_id: f.custom_field_id,
            required: f.required ? 1 : 0,
            add_alert: f.add_alert ? 1 : 0,
            position: i
          }));
        }

        const response = await axios.put(url, payload, config);
        const savedCustom = response.data.custom_statuses || [];
        const savedSystemOverrides = response.data.system_status_overrides || [];
        const savedInternalFields = response.data.internal_custom_fields || [];
        const savedRequirePhone = response.data.require_phone_internally ? 1 : 0;

        // Reconcile in-memory state with whatever the server persisted.
        this.workingRequirePhoneInternally = !!savedRequirePhone;

        // Keep localStorage group in sync so booking forms see the new rule
        // without requiring a re-login.
        try {
          if (getActiveGroupId() === Number(this.companyGroupId)) {
            patchActiveGroup({ require_phone_internally: savedRequirePhone });
          }
        } catch (e) { /* localStorage unavailable or malformed — ignore */ }

        this.$emit('saved', {
          customStatuses: savedCustom,
          systemStatusOverrides: savedSystemOverrides,
          internalCustomFields: savedInternalFields,
          requirePhoneInternally: savedRequirePhone
        });

        this.$notify({ type: 'success', message: 'Booking slot settings saved', timeout: 3000 });
        this.onClose();
      } catch (error) {
        console.error('Failed to save booking slot settings:', error);
        const message = error.response?.data?.message || 'Failed to save settings';
        swal('Error!', message, 'error');
      } finally {
        this.saving = false;
      }
    },

    // Guarded handler for overlay click + close button — prevents accidental
    // dismissal while the inline add-status form has unsaved input. The
    // explicit Cancel button bypasses this and calls onClose directly.
    onCloseRequest() {
      if (this.showAddStatusForm) return;
      this.onClose();
    },

    onClose() {
      this.$emit('update:show', false);
      this.$emit('close');
    }
  }
};
</script>

<style lang="scss" scoped>
.footer-actions {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
}

.status-list {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.status-item {
  padding: 8px 12px;
  background: rgba(255, 255, 255, 0.03);
  border-radius: 4px;
  border: 1px solid rgba(255, 255, 255, 0.05);

  &:hover {
    background: rgba(255, 255, 255, 0.06);
  }
}

.status-color-dot {
  width: 16px;
  height: 16px;
  border-radius: 4px;
  flex-shrink: 0;
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.status-name {
  font-size: 0.85rem;
  color: #fff;
}

.section-badge {
  display: inline-flex;
  align-items: center;
  padding: 2px 8px;
  font-size: 11px;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.75);
  background: rgba(255, 255, 255, 0.08);
  border-radius: 10px;
  line-height: 1.4;
  letter-spacing: 0.02em;

  &--accent {
    color: #e14eca;
    background: rgba(225, 78, 202, 0.15);
  }
}

.status-empty {
  padding: 12px;
  text-align: center;
  color: rgba(255, 255, 255, 0.5);
  background: rgba(255, 255, 255, 0.02);
  border-radius: 4px;
  border: 1px dashed rgba(255, 255, 255, 0.1);
  font-size: 0.85rem;
}

.custom-status-form {
  padding: 14px;
  background: rgba(255, 255, 255, 0.03);
  border-radius: 6px;
  border: 1px solid rgba(225, 78, 202, 0.25);
}

.toggle-row {
  display: flex;
  align-items: center;

  label {
    color: rgba(255, 255, 255, 0.85);
    font-size: 13px;
  }
}

.skeleton-section {
  padding: 16px;
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 8px;
}
</style>

<style lang="scss">
.white-content .bss-drawer {
  .status-item {
    background: rgba(0, 0, 0, 0.03);
    border-color: rgba(0, 0, 0, 0.08);

    &:hover {
      background: rgba(0, 0, 0, 0.06);
    }
  }

  .status-color-dot {
    border-color: rgba(0, 0, 0, 0.15);
  }

  .status-name {
    color: #344675;
  }

  .status-empty {
    color: #525f7f;
    background: rgba(0, 0, 0, 0.02);
    border-color: rgba(0, 0, 0, 0.08);
  }

  .section-badge {
    color: #525f7f;
    background: rgba(0, 0, 0, 0.06);

    &--accent {
      color: #e14eca;
      background: rgba(225, 78, 202, 0.12);
    }
  }

  .custom-status-form {
    background: rgba(0, 0, 0, 0.02);
    border-color: rgba(225, 78, 202, 0.35);
  }

  .toggle-row label {
    color: #525f7f;
  }

  .skeleton-section {
    background: #f8f9fa;
    border-color: #e3e3e3;
  }
}
</style>
